import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Search, Download, FileText, RefreshCw, Filter, X, Check, ShieldCheck, ChevronRight, Mail, Phone,
  Flame, CalendarCheck, CalendarX, Send, Copy, Link2, AlertTriangle,
} from "lucide-react";
import { useAdmin } from "../../admin/AdminContext";
import { AdminLayout } from "../../admin/AdminLayout";
import { Input } from "../../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";

const STATUS_OPTIONS = [
  { value: "new", label: "Nieuw", color: "bg-accent/15 text-accent" },
  { value: "contacted", label: "Gecontacteerd", color: "bg-blue-500/10 text-blue-600 dark:text-blue-300" },
  { value: "qualified", label: "Gekwalificeerd", color: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-300" },
  { value: "closed", label: "Gesloten", color: "bg-muted text-muted-foreground" },
];

const BOOKING_OPTIONS = [
  { value: "pending", label: "In afwachting" },
  { value: "booked", label: "Geboekt" },
  { value: "no_show", label: "No-show" },
];

const LABEL_STYLE = {
  hot: "bg-red-500/10 text-red-600 dark:text-red-300 border-red-500/30",
  medium: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30",
  cool: "bg-slate-500/10 text-slate-600 dark:text-slate-300 border-slate-500/30",
  intake: "bg-accent/10 text-accent border-accent/30",
  contact: "bg-secondary text-muted-foreground border-border",
  urgent: "bg-red-500/10 text-red-600 dark:text-red-300 border-red-500/30",
  luxembourg: "bg-blue-500/10 text-blue-600 dark:text-blue-300 border-blue-500/30",
  expat: "bg-teal-500/10 text-teal-600 dark:text-teal-300 border-teal-500/30",
  business: "bg-violet-500/10 text-violet-600 dark:text-violet-300 border-violet-500/30",
  incomplete: "bg-orange-500/10 text-orange-600 dark:text-orange-300 border-orange-500/30",
  booked: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  "no-show": "bg-rose-500/10 text-rose-700 dark:text-rose-300 border-rose-500/30",
  "direct-booking": "bg-purple-500/10 text-purple-700 dark:text-purple-300 border-purple-500/30",
};

const statusMeta = (s) => STATUS_OPTIONS.find((o) => o.value === s) || STATUS_OPTIONS[0];

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("nl-NL", { dateStyle: "medium", timeStyle: "short" });
}

function StatusPill({ value }) {
  const meta = statusMeta(value);
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10.5px] uppercase tracking-[0.15em] font-bold ${meta.color}`}>
      {meta.label}
    </span>
  );
}

function LabelPill({ value }) {
  const cls = LABEL_STYLE[value] || "bg-secondary text-muted-foreground border-border";
  return (
    <span className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[10px] uppercase tracking-[0.15em] font-bold ${cls}`} data-testid={`label-${value}`}>
      {value === "hot" && <Flame className="h-2.5 w-2.5" />}
      {value}
    </span>
  );
}

function ScoreBadge({ score }) {
  const color =
    score >= 70 ? "text-red-600 dark:text-red-300" :
    score >= 45 ? "text-amber-600 dark:text-amber-300" :
    "text-muted-foreground";
  return (
    <span className={`font-display text-base font-bold ${color}`} data-testid="score-value">
      {score ?? 0}
    </span>
  );
}

function LeadDrawer({ lead, onClose, onStatusChange, onBookingChange, onDownloadPdf, onSendIncomplete, apiBase, authHeaders }) {
  const [copied, setCopied] = useState(false);
  if (!lead) return null;

  const updateUrl = lead.update_token
    ? `${window.location.origin}/intake/${lead.update_token}`
    : null;

  const copyUpdateLink = async () => {
    if (!updateUrl) return;
    try {
      await navigator.clipboard.writeText(updateUrl);
      setCopied(true);
      toast.success("Link gekopieerd");
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error("Kopiëren mislukt");
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="lead-drawer">
      <button type="button" aria-label="Sluiten" onClick={onClose} className="flex-1 bg-foreground/30 backdrop-blur-sm" />
      <aside className="w-full max-w-[600px] bg-background border-l border-border overflow-y-auto">
        <div className="sticky top-0 z-10 border-b border-border bg-background/90 backdrop-blur px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <ScoreBadge score={lead.score} />
            {(lead.labels || []).slice(0, 5).map((l) => <LabelPill key={l} value={l} />)}
          </div>
          <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground" data-testid="lead-drawer-close">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-6 py-6 space-y-6">
          <div>
            <p className="text-xs uppercase tracking-[0.22em] font-bold text-muted-foreground">Aanvrager</p>
            <h2 className="mt-2 text-2xl tracking-tight font-medium text-foreground">{lead.name}</h2>
            <div className="mt-3 grid gap-1.5 text-sm">
              <a href={`mailto:${lead.email}`} className="inline-flex items-center gap-2 text-foreground hover:text-accent transition-colors">
                <Mail className="h-3.5 w-3.5 text-accent" />{lead.email}
              </a>
              {lead.phone && (
                <a href={`tel:${lead.phone}`} className="inline-flex items-center gap-2 text-foreground hover:text-accent transition-colors">
                  <Phone className="h-3.5 w-3.5 text-accent" />{lead.phone}
                </a>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-x-6 gap-y-4 border-y border-border py-5">
            <Field label="Land / regio" value={lead.country} />
            <Field label="Gewenste stad" value={lead.city} />
            <Field label="Type pand" value={lead.property_type} />
            <Field label="Budget" value={lead.budget} />
            <Field label="Timing" value={lead.timing} />
            <Field label="Taal" value={lead.language} />
            <Field label="Bron" value={lead.lead_source} />
            <Field label="Ontvangen" value={formatDate(lead.created_at)} />
          </div>

          {lead.message && (
            <div>
              <p className="text-xs uppercase tracking-[0.22em] font-bold text-muted-foreground">Bericht</p>
              <p className="mt-2 text-sm text-foreground/90 leading-relaxed whitespace-pre-line">{lead.message}</p>
            </div>
          )}

          {(lead.documents || []).length > 0 && (
            <div>
              <p className="text-xs uppercase tracking-[0.22em] font-bold text-muted-foreground">Documenten</p>
              <ul className="mt-3 space-y-2" data-testid="lead-docs">
                {lead.documents.map((d, i) => (
                  <li key={i} className="flex items-center gap-2.5 text-sm border-t border-border pt-2.5">
                    <FileText className="h-3.5 w-3.5 text-accent" />
                    <a
                      href={`${apiBase}/admin/leads/${lead.id}/documents/${encodeURIComponent(d.filename)}`}
                      target="_blank"
                      rel="noopener"
                      onClick={async (e) => {
                        e.preventDefault();
                        try {
                          const res = await axios.get(`${apiBase}/admin/leads/${lead.id}/documents/${encodeURIComponent(d.filename)}`, {
                            headers: authHeaders, responseType: "blob",
                          });
                          const url = window.URL.createObjectURL(res.data);
                          const a = document.createElement("a");
                          a.href = url; a.download = d.filename;
                          document.body.appendChild(a); a.click(); a.remove();
                          window.URL.revokeObjectURL(url);
                        } catch { toast.error("Download mislukt"); }
                      }}
                      className="truncate flex-1 text-foreground hover:text-accent transition-colors"
                    >
                      {d.filename}
                    </a>
                    <span className="text-xs text-muted-foreground">{Math.round((d.size || 0) / 1024)} KB</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {lead.request_type === "intake" && lead.terms_accepted && (
            <div className="rounded-md border border-accent/30 bg-accent/5 p-5" data-testid="acceptance-proof">
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-accent">
                <ShieldCheck className="h-3.5 w-3.5" /> Clickwrap acceptance proof
              </div>
              <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
                <Field label="Aanvaard op" value={formatDate(lead.terms_accepted_at)} />
                <Field label="Versie voorwaarden" value={lead.terms_version} />
                <Field label="Taal" value={lead.terms_language} />
                <Field label="URL" value={lead.terms_url ? (
                  <a href={lead.terms_url} target="_blank" rel="noopener" className="text-foreground underline underline-offset-4 hover:text-accent">Bekijken ↗</a>
                ) : "—"} />
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <p className="text-xs uppercase tracking-[0.22em] font-bold text-muted-foreground mb-2">Status</p>
              <Select value={lead.status} onValueChange={(v) => onStatusChange(lead.id, v)}>
                <SelectTrigger data-testid="lead-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value} data-testid={`status-opt-${o.value}`}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.22em] font-bold text-muted-foreground mb-2">Booking</p>
              <Select value={lead.booking_status || "pending"} onValueChange={(v) => onBookingChange(lead.id, v)}>
                <SelectTrigger data-testid="lead-booking-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BOOKING_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value} data-testid={`booking-opt-${o.value}`}>{o.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Action buttons */}
          <div className="grid sm:grid-cols-2 gap-3">
            <button type="button" onClick={() => onDownloadPdf(lead)} data-testid="lead-pdf-download" className="btn-primary justify-center">
              <FileText className="h-4 w-4" /> PDF samenvatting
            </button>
            {lead.request_type === "intake" && (
              <button type="button" onClick={() => onSendIncomplete(lead)} data-testid="lead-send-incomplete" className="btn-ghost justify-center">
                <Send className="h-4 w-4" /> Stuur aanvulverzoek
              </button>
            )}
          </div>

          {updateUrl && (
            <div className="rounded-md border border-border bg-secondary/40 p-4">
              <p className="text-xs uppercase tracking-[0.22em] font-bold text-muted-foreground">Self-service link</p>
              <div className="mt-2 flex items-center gap-2">
                <code className="flex-1 text-xs text-foreground/80 truncate">{updateUrl}</code>
                <button
                  type="button"
                  onClick={copyUpdateLink}
                  data-testid="copy-update-link"
                  className="inline-flex items-center gap-1 text-xs text-accent hover:text-accent/80 transition-colors"
                >
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied ? "Gekopieerd" : "Kopieer"}
                </button>
                <a
                  href={updateUrl}
                  target="_blank"
                  rel="noopener"
                  className="inline-flex items-center gap-1 text-xs text-accent hover:text-accent/80 transition-colors"
                  data-testid="open-update-link"
                >
                  <Link2 className="h-3.5 w-3.5" /> Open
                </a>
              </div>
            </div>
          )}
        </div>
      </aside>
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">{label}</p>
      <p className="mt-1 text-sm text-foreground/90">{value || "—"}</p>
    </div>
  );
}

export default function AdminDashboard() {
  const { token, authHeaders, api } = useAdmin();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ total: 0, contact: 0, intake: 0, hot: 0, incomplete: 0, awaiting_booking: 0 });
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    request_type: "all", status: "all", score: "all", booking_status: "all",
    country: "", q: "", date_from: "", date_to: "", incomplete: false,
  });
  const [selected, setSelected] = useState(null);

  useEffect(() => { document.title = "Admin — Tip Top Property"; }, []);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.request_type !== "all") params.set("request_type", filters.request_type);
      if (filters.status !== "all") params.set("status", filters.status);
      if (filters.score !== "all") params.set("score", filters.score);
      if (filters.booking_status !== "all") params.set("booking_status", filters.booking_status);
      if (filters.country.trim()) params.set("country", filters.country.trim());
      if (filters.q.trim()) params.set("q", filters.q.trim());
      if (filters.date_from) params.set("date_from", filters.date_from);
      if (filters.date_to) params.set("date_to", filters.date_to);
      if (filters.incomplete) params.set("incomplete", "true");
      const { data } = await axios.get(`${api}/admin/leads?${params.toString()}`, { headers: authHeaders });
      setItems(data.items || []);
      setCounts(data.counts || {});
    } catch (err) {
      toast.error("Kon leads niet laden.");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [api, authHeaders, filters, token]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const id = setTimeout(load, 350);
    return () => clearTimeout(id);
  }, [filters.q, filters.country]); // eslint-disable-line react-hooks/exhaustive-deps

  const onStatusChange = async (id, status) => {
    try {
      await axios.patch(`${api}/admin/leads/${id}/status`, { status }, { headers: authHeaders });
      setItems((rows) => rows.map((r) => (r.id === id ? { ...r, status } : r)));
      if (selected?.id === id) setSelected((s) => ({ ...s, status }));
      toast.success("Status bijgewerkt.");
    } catch { toast.error("Status update mislukt."); }
  };

  const onBookingChange = async (id, status) => {
    try {
      const { data } = await axios.patch(`${api}/admin/leads/${id}/booking`, { status }, { headers: authHeaders });
      setItems((rows) => rows.map((r) => (r.id === id ? { ...r, booking_status: status, score: data.score, labels: data.labels } : r)));
      if (selected?.id === id) setSelected((s) => ({ ...s, booking_status: status, score: data.score, labels: data.labels }));
      toast.success(status === "booked" ? "Booking bevestigingsmail verstuurd." : status === "no_show" ? "No-show follow-up verstuurd." : "Booking bijgewerkt.");
    } catch (err) {
      toast.error("Booking update mislukt.");
      console.error(err);
    }
  };

  const onSendIncomplete = async (lead) => {
    try {
      const { data } = await axios.post(`${api}/admin/leads/${lead.id}/send-incomplete-email`, {}, { headers: authHeaders });
      toast.success(data.sent ? "Aanvulverzoek verstuurd." : "Aanvulverzoek niet verstuurd (mail niet geconfigureerd).");
    } catch { toast.error("Versturen mislukt."); }
  };

  const onDownloadPdf = async (lead) => {
    try {
      const res = await axios.get(`${api}/admin/leads/${lead.id}/pdf`, { headers: authHeaders, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const safeName = (lead.name || "lead").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
      a.download = `tip-top-property-${lead.request_type || "lead"}-${safeName}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("PDF download mislukt."); }
  };

  const onExportCsv = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.request_type !== "all") params.set("request_type", filters.request_type);
      if (filters.status !== "all") params.set("status", filters.status);
      const res = await axios.get(`${api}/admin/leads.csv?${params.toString()}`, { headers: authHeaders, responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href = url; a.download = "tip-top-property-leads.csv";
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("CSV export mislukt."); }
  };

  const filtersBar = useMemo(() => (
    <div className="grid md:grid-cols-12 gap-3 items-end" data-testid="admin-filters">
      <div className="md:col-span-3">
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-1.5">Zoeken</p>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
          <Input value={filters.q} onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))} placeholder="Naam, e-mail, stad…" data-testid="admin-search" className="pl-9" />
        </div>
      </div>
      <div className="md:col-span-2">
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-1.5">Score</p>
        <Select value={filters.score} onValueChange={(v) => setFilters((f) => ({ ...f, score: v }))}>
          <SelectTrigger data-testid="filter-score"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle</SelectItem>
            <SelectItem value="hot" data-testid="filter-score-hot">Hot (≥70)</SelectItem>
            <SelectItem value="medium">Medium (45-69)</SelectItem>
            <SelectItem value="cool">Cool (&lt;45)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="md:col-span-2">
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-1.5">Type</p>
        <Select value={filters.request_type} onValueChange={(v) => setFilters((f) => ({ ...f, request_type: v }))}>
          <SelectTrigger data-testid="filter-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle ({counts.total || 0})</SelectItem>
            <SelectItem value="intake">Intake ({counts.intake || 0})</SelectItem>
            <SelectItem value="contact">Contact ({counts.contact || 0})</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="md:col-span-2">
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-1.5">Booking</p>
        <Select value={filters.booking_status} onValueChange={(v) => setFilters((f) => ({ ...f, booking_status: v }))}>
          <SelectTrigger data-testid="filter-booking"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle</SelectItem>
            <SelectItem value="pending" data-testid="filter-booking-pending">In afwachting ({counts.awaiting_booking || 0})</SelectItem>
            <SelectItem value="booked">Geboekt</SelectItem>
            <SelectItem value="no_show">No-show</SelectItem>
          </SelectContent>
        </Select>
      </div>
      <div className="md:col-span-2">
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-1.5">Status</p>
        <Select value={filters.status} onValueChange={(v) => setFilters((f) => ({ ...f, status: v }))}>
          <SelectTrigger data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Alle</SelectItem>
            {STATUS_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="md:col-span-3">
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-1.5">Land</p>
        <Input value={filters.country} onChange={(e) => setFilters((f) => ({ ...f, country: e.target.value }))} placeholder="bv. Luxembourg" data-testid="filter-country" />
      </div>
      <div className="md:col-span-2">
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-1.5">Vanaf</p>
        <Input type="date" value={filters.date_from} onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))} data-testid="filter-date-from" />
      </div>
      <div className="md:col-span-2">
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-1.5">Tot</p>
        <Input type="date" value={filters.date_to} onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))} data-testid="filter-date-to" />
      </div>
      <div className="md:col-span-3 flex items-center">
        <label className="inline-flex items-center gap-2 text-sm cursor-pointer mt-5" data-testid="filter-incomplete-wrap">
          <input
            type="checkbox"
            checked={filters.incomplete}
            onChange={(e) => setFilters((f) => ({ ...f, incomplete: e.target.checked }))}
            data-testid="filter-incomplete"
            className="h-4 w-4 cursor-pointer rounded border-border accent-accent"
          />
          <span className="text-foreground/85">Alleen onvolledige intakes ({counts.incomplete || 0})</span>
        </label>
      </div>
    </div>
  ), [counts, filters]);

  return (
    <AdminLayout>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="eyebrow">Dashboard</p>
          <h1 className="mt-3 text-3xl md:text-4xl tracking-tight font-medium text-foreground">Leads & intakes</h1>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={load} className="btn-ghost" data-testid="admin-refresh" disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
          <button type="button" onClick={onExportCsv} className="btn-primary" data-testid="admin-export-csv">
            <Download className="h-3.5 w-3.5" />
            Export CSV
          </button>
        </div>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-5 gap-3" data-testid="admin-stats">
        <StatCard label="Totaal" value={counts.total} />
        <StatCard label="Intakes" value={counts.intake} />
        <StatCard label="Hot leads" value={counts.hot} highlight icon={<Flame className="h-3.5 w-3.5 text-red-500" />} />
        <StatCard label="Onvolledig" value={counts.incomplete} icon={<AlertTriangle className="h-3.5 w-3.5 text-orange-500" />} />
        <StatCard label="Wacht op booking" value={counts.awaiting_booking} icon={<CalendarX className="h-3.5 w-3.5 text-amber-500" />} />
      </div>

      <div className="mt-6 card-soft p-5 md:p-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-muted-foreground mb-4">
          <Filter className="h-3.5 w-3.5" /> Filters
        </div>
        {filtersBar}
      </div>

      <div className="mt-6 rounded-md border border-border bg-card overflow-hidden" data-testid="admin-table">
        <div className="hidden md:grid grid-cols-12 gap-3 px-5 py-3 border-b border-border bg-secondary/50 text-[10.5px] uppercase tracking-[0.18em] font-bold text-muted-foreground">
          <div className="col-span-1">Score</div>
          <div className="col-span-3">Aanvrager</div>
          <div className="col-span-3">Labels</div>
          <div className="col-span-2">Booking</div>
          <div className="col-span-2">Ontvangen</div>
          <div className="col-span-1 text-right"></div>
        </div>
        {items.length === 0 ? (
          <div className="px-5 py-12 text-center text-sm text-muted-foreground" data-testid="admin-table-empty">
            {loading ? "Bezig met laden…" : "Geen leads gevonden voor deze filters."}
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {items.map((lead) => (
              <li
                key={lead.id}
                data-testid={`lead-row-${lead.id}`}
                className="grid grid-cols-12 gap-3 items-center px-5 py-4 hover:bg-secondary/30 transition-colors cursor-pointer"
                onClick={() => setSelected(lead)}
              >
                <div className="col-span-2 md:col-span-1"><ScoreBadge score={lead.score} /></div>
                <div className="col-span-10 md:col-span-3 min-w-0">
                  <p className="text-sm text-foreground font-medium truncate">{lead.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{lead.email} · {lead.country || "—"}</p>
                </div>
                <div className="col-span-12 md:col-span-3 flex flex-wrap items-center gap-1">
                  {(lead.labels || []).slice(0, 4).map((l) => <LabelPill key={l} value={l} />)}
                </div>
                <div className="col-span-6 md:col-span-2 flex items-center gap-1.5 text-xs">
                  {lead.booking_status === "booked" ? (
                    <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-300"><CalendarCheck className="h-3.5 w-3.5" /> Geboekt</span>
                  ) : lead.booking_status === "no_show" ? (
                    <span className="inline-flex items-center gap-1 text-rose-600 dark:text-rose-300"><CalendarX className="h-3.5 w-3.5" /> No-show</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-muted-foreground"><CalendarX className="h-3.5 w-3.5" /> Pending</span>
                  )}
                </div>
                <div className="col-span-5 md:col-span-2 text-xs text-muted-foreground">{formatDate(lead.created_at)}</div>
                <div className="col-span-1 flex justify-end">
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <LeadDrawer
        lead={selected}
        onClose={() => setSelected(null)}
        onStatusChange={onStatusChange}
        onBookingChange={onBookingChange}
        onDownloadPdf={onDownloadPdf}
        onSendIncomplete={onSendIncomplete}
        apiBase={api}
        authHeaders={authHeaders}
      />
    </AdminLayout>
  );
}

function StatCard({ label, value, highlight, icon }) {
  return (
    <div className={`card-soft p-5 ${highlight ? "border-accent/40" : ""}`}>
      <div className="flex items-center gap-1.5">
        {icon}
        <p className="text-[10.5px] uppercase tracking-[0.22em] font-bold text-muted-foreground">{label}</p>
      </div>
      <p className={`mt-2 font-display text-3xl ${highlight ? "text-accent" : "text-foreground"}`}>{value ?? 0}</p>
    </div>
  );
}
